import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { fetchGTL, createGTL, deleteGTL } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Search, FolderKanban, Plus, Users, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

// Format date from YYYY-MM-DD to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) {
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

export default function GTLPage() {
  const [gtlList, setGtlList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statoFilter, setStatoFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedGTL, setSelectedGTL] = useState(null);
  const [newGTL, setNewGTL] = useState({ nome: '', tema: '', descrizione: '', data_inizio: '', data_fine: '', stato: 'attivo' });
  const [saving, setSaving] = useState(false);

  const { isSegretario, isAdminOrdine, isCoordinatoreGTL, user } = useAuth();
  const navigate = useNavigate();

  const loadGTL = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchGTL({ search, stato: statoFilter === 'all' ? '' : statoFilter, page, limit: 20 });
      let items = data.items;
      
      // Admin Ordine: solo GTL attivi
      if (isAdminOrdine()) {
        items = items.filter(g => g.stato === 'attivo');
      }
      
      // Coordinatore GTL: solo il proprio GTL
      if (isCoordinatoreGTL() && user?.gtl_id) {
        items = items.filter(g => g.id === user.gtl_id);
      }
      
      // Sort GTL alphabetically
      items.sort((a, b) => a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' }));
      
      setGtlList(items);
      setTotal(items.length);
    } catch (error) {
      console.error('Error loading GTL:', error);
    } finally {
      setLoading(false);
    }
  }, [search, statoFilter, page, isAdminOrdine, isCoordinatoreGTL, user]);

  useEffect(() => {
    loadGTL();
  }, [loadGTL]);

  const handleCreateGTL = async () => {
    if (!newGTL.nome || !newGTL.tema) {
      toast.error('Nome e tema sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      await createGTL(newGTL);
      toast.success('GTL creato con successo');
      setCreateModalOpen(false);
      setNewGTL({ nome: '', tema: '', descrizione: '', data_inizio: '', data_fine: '', stato: 'attivo' });
      loadGTL();
    } catch (error) {
      console.error('Error creating GTL:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGTL = async () => {
    if (!selectedGTL) return;
    try {
      await deleteGTL(selectedGTL.id);
      toast.success('GTL eliminato con successo');
      setDeleteConfirmOpen(false);
      setSelectedGTL(null);
      loadGTL();
    } catch (error) {
      console.error('Error deleting GTL:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'attivo':
        return <span className="badge-success">Attivo</span>;
      case 'concluso':
        return <span className="badge-neutral">Concluso</span>;
      case 'sospeso':
        return <span className="badge-warning">Sospeso</span>;
      default:
        return <span className="badge-neutral">{stato}</span>;
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="gtl-page">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Gruppi di Lavoro Temporanei</h1>
            <p className="text-slate-500 mt-1">{total} GTL registrati</p>
          </div>
          {isSegretario() && (
            <Button onClick={() => setCreateModalOpen(true)} data-testid="create-gtl-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo GTL
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="card-institutional mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Cerca per nome o tema..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-gtl-input"
              />
            </div>
            <Select value={statoFilter} onValueChange={setStatoFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="stato-gtl-filter">
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="attivo">Attivo</SelectItem>
                <SelectItem value="sospeso">Sospeso</SelectItem>
                <SelectItem value="concluso">Concluso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* GTL Grid */}
      {loading ? (
        <div className="p-8 text-center">
          <div className="spinner mx-auto" />
        </div>
      ) : gtlList.length === 0 ? (
        <Card className="card-institutional">
          <CardContent className="py-12">
            <div className="empty-state">
              <FolderKanban className="empty-state-icon" />
              <p className="empty-state-title">Nessun GTL trovato</p>
              <p className="empty-state-description">
                {isSegretario() ? 'Crea il primo Gruppo di Lavoro Temporaneo' : 'Nessun GTL corrisponde ai criteri di ricerca'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gtlList.map((gtl) => (
            <Card key={gtl.id} className="card-institutional" data-testid={`gtl-card-${gtl.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-slate-900 mb-1">{gtl.nome}</h3>
                    <p className="text-sm text-slate-500">{gtl.tema}</p>
                  </div>
                  {getStatoBadge(gtl.stato)}
                </div>
                
                {gtl.descrizione && (
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{gtl.descrizione}</p>
                )}
                
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{gtl.membri_count || 0} membri</span>
                  </div>
                  {gtl.data_inizio && (
                    <span>Dal {formatDate(gtl.data_inizio)}</span>
                  )}
                </div>
                
                <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(`/gtl/${gtl.id}`)}
                    data-testid={`view-gtl-${gtl.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Dettagli
                  </Button>
                  {isSegretario() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => { setSelectedGTL(gtl); setDeleteConfirmOpen(true); }}
                      data-testid={`delete-gtl-${gtl.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg" data-testid="create-gtl-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuovo Gruppo di Lavoro Temporaneo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome GTL *</Label>
              <Input
                value={newGTL.nome}
                onChange={(e) => setNewGTL({ ...newGTL, nome: e.target.value })}
                placeholder="Es: GTL Digitalizzazione"
                data-testid="gtl-nome-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Tema *</Label>
              <Input
                value={newGTL.tema}
                onChange={(e) => setNewGTL({ ...newGTL, tema: e.target.value })}
                placeholder="Es: Trasformazione Digitale"
                data-testid="gtl-tema-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={newGTL.descrizione}
                onChange={(e) => setNewGTL({ ...newGTL, descrizione: e.target.value })}
                placeholder="Descrizione delle attività del gruppo..."
                rows={3}
                data-testid="gtl-descrizione-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Inizio</Label>
                <Input
                  type="date"
                  value={newGTL.data_inizio}
                  onChange={(e) => setNewGTL({ ...newGTL, data_inizio: e.target.value })}
                  data-testid="gtl-data-inizio-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fine</Label>
                <Input
                  type="date"
                  value={newGTL.data_fine}
                  onChange={(e) => setNewGTL({ ...newGTL, data_fine: e.target.value })}
                  data-testid="gtl-data-fine-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Stato</Label>
              <Select value={newGTL.stato} onValueChange={(v) => setNewGTL({ ...newGTL, stato: v })}>
                <SelectTrigger data-testid="gtl-stato-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="concluso">Concluso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateGTL} disabled={saving} data-testid="save-gtl-btn">
              {saving ? 'Creazione...' : 'Crea GTL'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent data-testid="delete-gtl-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il GTL "{selectedGTL?.nome}"? 
              Questa azione eliminerà anche tutti i membri associati e non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteGTL}
              data-testid="confirm-delete-gtl"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
